/**
 * The CRO watch the Worker posts with every preview point: the memo's objections and warnings, as the strip shows them.
 * This is the one place that reads the engine's `croWatch` findings.
 */
import { kpiLabel } from "../format/labels";
import { DECISION_LABEL, familyOfPath, type DecisionFamily, type DecisionId, type KpiId } from "@bsim/domain";
import type { CroWatch as EngineWatch } from "@bsim/engine";

export interface CroWatchItem {
  readonly severity: "objection" | "warn";
  /** The measure's name as the memo quotes it ("Payout ratio"), its projected value and the limit it is read against. */
  readonly label: string;
  readonly kpi: KpiId | null;
  readonly projected: number;
  readonly limit: number;
  /** The decision family whose tab holds the way out: the finding's own `decisionIds` when it carries them, else its "Decision: Buyback target." line. */
  readonly family: DecisionFamily | null;
}

/** The gate warning (memo item `camelsFlag` on `camelsComposite`): the memo's own sentence and the desks of the components that stepped, where "Lift it" goes. */
export interface CroWatchFlag { readonly severity: "objection" | "warn"; readonly text: string; readonly families: readonly DecisionFamily[] }

export interface CroWatch {
  readonly objections: number; readonly warns: number; readonly items: readonly CroWatchItem[]; readonly flags: readonly CroWatchFlag[];
  /** The findings with no number against a limit (a flag, a card\u2019s risk), in the memo\u2019s own words: the strip lists what the memo says, so the two never disagree. */
  readonly others?: readonly string[];
  /** The memo's information lines (`info`): not objections or warnings, but "watch items" the memo lists; counted in the header, never "nothing". */
  readonly notes?: readonly string[];
  /** The team's own risk officer's open objections (not the engine's reading): the strip never says "nothing to object to" while one is open. */
  readonly human?: number;
  /** Every finding the memo rules read on this plan, whatever its severity: the one source the risk dashboard (the panel, the drawer, the Review card and the memo receipt) builds its rows from, so a rating that moves is a row the moment the run says so. */
  readonly findings?: readonly Finding[];
  /** How many of the warnings are the Management rating notch (the own-limit preview: a camelsFlag warn with a limit and no projected figure): a warn is never an objection (P5-D6), so the header says what this one carries. */
  readonly notches?: number;
  /** The player's own limit the plan breaks (the memo's own-limit preview, always a warning: P5-D14): the measure, the limit and the quarter-ends of the year it is passed at, so the header can lead with it. */
  readonly ownLimit?: { readonly kpi: KpiId; readonly limit: number; readonly quarterEnds: number; /** The rate path it is found on ("the consensus path", "the higher-rate path"). */ readonly path?: string };
}

/** What the Worker posts with a point: the watch, and whether the draft buys back while the outlook is paused (the no-change plan pauses, so the baseline differs from a plan left as it was). */
export interface PreviewWatch extends CroWatch { readonly buybackPaused: boolean }

const DECISIONS_LINE = /(?:Decisions?|What moves it): ([^.]*)\./;
const SUBJECT = /^(.+?) (?:is|are) projected\b/;

// A finding names its decision through `decisionIds` (ADR-0049); a finding without them (memos stored before) falls back to the label in its own text.
const familyOf = (f: Finding): DecisionFamily | null => {
  const names = DECISIONS_LINE.exec(f.text)?.[1] ?? "";
  const id = f.decisionIds?.[0] ?? (Object.keys(DECISION_LABEL) as DecisionId[]).find((d) => names.includes(DECISION_LABEL[d]));
  return id === undefined ? null : familyOfPath(id);
};

export type Finding = {
  readonly kind?: string | undefined;
  readonly severity: "info" | "warn" | "objection"; readonly kpi?: KpiId | undefined; readonly projected?: number | undefined; readonly limit?: number | undefined; readonly text: string;
  readonly decisionIds?: readonly DecisionId[] | undefined;
};

/** The strip's reading of a list of findings, the engine's or the memo's: objections and warnings counted, the ones with a number against a limit listed. */
/** The own-limit preview's sentence ("Your plan puts X beyond your -20.0% limit at 4 quarter-ends this year (...)"): the count it prints, absent for the several-at-once and record-only sentences. */
const OWN_LIMIT_COUNT = /^Your plan puts .+? beyond your .+? limit at (\d+) quarter-ends? this year(?: on (the [a-z-]+ path))?/;

export function watchOfFindings(findings: readonly Finding[]): CroWatch {
  const flagged = findings.filter((f) => f.severity !== "info");
  const isGate = (f: Finding): boolean => f.kind === "camelsFlag" && f.kpi === "camelsComposite";
  const flags = flagged.filter(isGate).map((f): CroWatchFlag => ({
    severity: f.severity === "objection" ? "objection" : "warn", text: f.text,
    families: [...new Set((f.decisionIds ?? []).map((d) => familyOfPath(d)))],
  }));
  const items = flagged.filter((f) => !isGate(f)).flatMap((i): CroWatchItem[] => {
    const subject = SUBJECT.exec(i.text)?.[1];
    const label = i.kpi !== undefined ? kpiLabel(i.kpi) : subject === undefined ? undefined : `${subject.charAt(0).toUpperCase()}${subject.slice(1)}`;
    return i.projected === undefined || i.limit === undefined || label === undefined ? [] : [{ severity: i.severity === "objection" ? "objection" : "warn", label, kpi: i.kpi ?? null, projected: i.projected, limit: i.limit, family: familyOf(i) }];
  });
  const others = flagged.filter((f) => !isGate(f) && (f.projected === undefined || f.limit === undefined)).map((f) => f.text);
  const notes = findings.filter((f) => f.severity === "info").map((f) => f.text);
  const notches = flagged.filter((f) => f.severity === "warn" && f.kind === "camelsFlag" && f.kpi !== undefined && f.limit !== undefined && f.projected === undefined).length;
  const own = flagged.find((f) => f.severity === "warn" && f.kind === "camelsFlag" && f.kpi !== undefined && f.limit !== undefined && f.projected === undefined && OWN_LIMIT_COUNT.test(f.text));
  const ownLimit = own === undefined ? undefined : { kpi: own.kpi as KpiId, limit: own.limit as number, quarterEnds: Number(OWN_LIMIT_COUNT.exec(own.text)?.[1]), ...(OWN_LIMIT_COUNT.exec(own.text)?.[2] === undefined ? {} : { path: OWN_LIMIT_COUNT.exec(own.text)?.[2] as string }) };
  return { ...(ownLimit === undefined ? {} : { ownLimit }), findings, objections: flagged.filter((f) => f.severity === "objection").length, warns: flagged.filter((f) => f.severity === "warn").length, items, flags, others, notes, notches };
}

export const watchOf = (watch: EngineWatch): CroWatch =>
  watchOfFindings([
    ...watch.objections.map((i) => ({ ...i, severity: "objection" as const })),
    ...watch.warnings.map((i) => ({ ...i, severity: "warn" as const })),
    ...(watch.notes ?? []).map((i) => ({ ...i, severity: "info" as const })),
  ]);

type MemoFound = { readonly memo: { readonly items: readonly Finding[] } } | null;

/**
 * The strip beside the memo reads the memo: on the review page its findings are the strip's, and none yet means no strip (never "nothing" for a memo still to come).
 * On Decide the preview's own watch stands, unless it finds nothing while the memo for this draft version has findings.
 */
export function watchBesideMemo(preview: PreviewWatch | null, memo: MemoFound, reviewing: boolean, humanOpen = 0): CroWatch | null {
  const fromMemo = memo === null ? null : watchOfFindings(memo.memo.items);
  const base = reviewing ? fromMemo
    : fromMemo !== null && preview !== null && preview.objections + preview.warns === 0 && fromMemo.objections + fromMemo.warns > 0 ? fromMemo : preview;
  // One risk officer: the engine's findings, the memo's information lines and the human officer's open objections read as one strip.
  if (base === null) return humanOpen > 0 ? { objections: 0, warns: 0, items: [], flags: [], human: humanOpen } : null;
  const notes = base.notes ?? fromMemo?.notes ?? [];
  return humanOpen === 0 && notes.length === (base.notes?.length ?? 0) ? base : { ...base, notes, human: humanOpen };
}
